'use client';

import { useCallback, useEffect, useState } from 'react';

import {
  CLIENT_ERROR_CODE,
  parseApiErrorEnvelope,
  REQUEST_ERROR,
  type ThreadSearchResult,
} from '@/lib/api';
import { searchDocs } from '@/lib/api/generated';

/** Coalesces keystrokes before asking Postgres to search post bodies. */
const DOC_SEARCH_DEBOUNCE_MILLISECONDS = 300;

/**
 * Pauses automatic thread search when the input is more useful as a draft.
 * This UI cutoff is below the backend safeguard in `rust/server/src/api/doc_search.rs`.
 * Count trimmed Unicode code points, as the backend does.
 */
const MAX_AUTOMATIC_DOC_SEARCH_CHARACTERS = 100;

/** Latest post-search response and its user-facing lifecycle state. */
interface DocSearchState {
  /** Query that produced this state, used to suppress results from previous input. */
  query: string;
  /** Conversations in server-provided relevance order with authorized post excerpts. */
  results: ThreadSearchResult[];
  /** Separates inactive input and length limits from pending, completed, and failed requests. */
  status: 'idle' | 'loading' | 'success' | 'too_long' | 'error';
}

/** Searches conversations while cancelling and ignoring responses for superseded input. */
export function useDocSearch(query: string): Omit<DocSearchState, 'query'> & { retry: () => void } {
  const normalizedQuery = query.trim();
  const isQueryTooLong = Array.from(normalizedQuery).length > MAX_AUTOMATIC_DOC_SEARCH_CHARACTERS;
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<DocSearchState>({
    query: '',
    results: [],
    status: 'idle',
  });
  const retry = useCallback(() => {
    if (!isQueryTooLong && state.query === normalizedQuery && state.status === 'error') {
      setAttempt((previous) => previous + 1);
    }
  }, [isQueryTooLong, normalizedQuery, state.query, state.status]);

  useEffect(() => {
    if (!normalizedQuery || isQueryTooLong) {
      setState({
        query: normalizedQuery,
        results: [],
        status: isQueryTooLong ? 'too_long' : 'idle',
      });
      return;
    }
    const controller = new AbortController();
    setState({ query: normalizedQuery, results: [], status: 'loading' });
    const timeoutId = window.setTimeout(() => {
      const search = async () => {
        try {
          const results = await searchDocs(
            { query: normalizedQuery },
            { signal: controller.signal }
          );
          if (!controller.signal.aborted) {
            setState({ query: normalizedQuery, results, status: 'success' });
          }
        } catch (searchError) {
          if (!controller.signal.aborted) {
            // A server length rejection is an inactive search even if the server's limit differs.
            const envelope =
              typeof searchError === 'object' && searchError !== null && 'data' in searchError
                ? parseApiErrorEnvelope(searchError.data)
                : null;
            const isLengthRejection =
              envelope?.error.type === CLIENT_ERROR_CODE.InvalidRequestData &&
              envelope.error.reason === REQUEST_ERROR.SearchQueryTooLong;
            if (!isLengthRejection) console.error('Error searching posts:', searchError);
            setState({
              query: normalizedQuery,
              results: [],
              status: isLengthRejection ? 'too_long' : 'error',
            });
          }
        }
      };
      void search();
    }, DOC_SEARCH_DEBOUNCE_MILLISECONDS);
    return () => {
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, [normalizedQuery, isQueryTooLong, attempt]);

  // Hide superseded results immediately, before the effect cancels the previous search.
  if (!normalizedQuery || isQueryTooLong || state.query !== normalizedQuery) {
    return {
      results: [],
      status: !normalizedQuery ? 'idle' : isQueryTooLong ? 'too_long' : 'loading',
      retry,
    };
  }
  return { results: state.results, status: state.status, retry };
}
